import React, { memo, useCallback, useEffect, useState } from 'react';

import { ICheckboxGroup, INestedCheckboxGroupProps } from './NestedCheckboxGroup.types';

import CheckboxGroup from './CheckboxGroup';

const NestedCheckboxGroup = ({
	className,
	disabled,
	items,
	maxHeightOfNestedItems,
	onChange,
}: INestedCheckboxGroupProps) => {
	const [nestedCheckboxes, setNestedCheckboxes] = useState<ICheckboxGroup[]>([]);

	useEffect(() => {
		if (!nestedCheckboxes.length) {
			setNestedCheckboxes(items);
		}
	}, [items, nestedCheckboxes]);

	const toggleGroup = useCallback((groups: ICheckboxGroup[], name: string): ICheckboxGroup[] => {
		return groups.map(group => {
			if (group.value === name) {
				return { ...group, isCollapsed: !group.isCollapsed };
			}

			if (group.nestedItems && group.nestedItems.length) {
				return { ...group, nestedItems: toggleGroup(group.nestedItems, name) };
			}

			return group;
		});
	}, []);

	const handleGroupClick = useCallback((name: string) => {
		setNestedCheckboxes(prev => toggleGroup(prev, name));
	}, [toggleGroup]);

	const handleCheckboxClick = useCallback((checked: boolean, name: string) => {
		const updateItems = (items: ICheckboxGroup[], forceUpdate = false, newChecked = false): ICheckboxGroup[] => {
			return items.map(item => {
				const shouldUpdate = forceUpdate || item.value === name;

				if (shouldUpdate) {
					const updatedItem = { ...item, checked: forceUpdate ? newChecked : checked };

					if (item.nestedItems?.length) {
						const updatedNestedItems = updateItems(item.nestedItems, true, updatedItem.checked);
						const isIndefinite = updatedNestedItems
							.some(nestedItem => nestedItem.checked !== updatedItem.checked || nestedItem.isIndefinite);

						return {
							...updatedItem,
							nestedItems: updatedNestedItems,
							isIndefinite: isIndefinite,
						};
					}

					return updatedItem;
				} else if (item.nestedItems?.length) {
					const updatedNestedItems = updateItems(item.nestedItems);
					const allChecked = updatedNestedItems.every(nestedItem => nestedItem.checked);
					const anyChecked = updatedNestedItems.some(nestedItem => nestedItem.checked);
					const isIndefinite = !allChecked && anyChecked || updatedNestedItems
						.some(nestedItem => nestedItem.isIndefinite);

					return {
						...item,
						nestedItems: updatedNestedItems,
						isIndefinite: isIndefinite,
					};
				}

				return item;
			});
		};

		setNestedCheckboxes(prev => updateItems(prev));
	}, []);

	useEffect(() => {
		onChange(nestedCheckboxes);
	}, [nestedCheckboxes, onChange]);

	return (
		<div className={className}>
			{nestedCheckboxes.map((group) => (
				<CheckboxGroup
					key={group.value}
					disabled={disabled || group.disabled}
					group={group}
					maxHeightOfNestedItems={maxHeightOfNestedItems}
					onCheckboxClick={handleCheckboxClick}
					onGroupToggle={handleGroupClick}
				/>
			))}
		</div>
	);
};

export default memo(NestedCheckboxGroup);
